import { AppCardMedia } from '@app/components/base/app-card';
import { buildGoogleStaticMapUrl } from '@app/modules/fleet/balena-devices/utils/google-static-map-url';
import { FleetBalenaDeviceDto } from '@mi-front/api-v2';
import { Box, useTheme } from '@mui/material';
import { ReactNode, useEffect, useState } from 'react';
import { defineMessage, useIntl } from 'react-intl';

const mapAltMessage = defineMessage({
  id: 'fleet.devices.card.mapAlt',
  defaultMessage: 'Mapa de {name}',
});

const mapThumbSx = {
  height: 118,
  width: '100%',
  position: 'relative',
  overflow: 'hidden',
  bgcolor: 'grey.50',
  backgroundImage: `
    linear-gradient(rgba(0,0,0,0.04) 1px, transparent 1px),
    linear-gradient(90deg, rgba(0,0,0,0.04) 1px, transparent 1px)
  `,
  backgroundSize: '16px 16px',
} as const;

/**
 * Card map thumbnail: Google Static Maps image when available, CSS grid fallback otherwise.
 * Uses a dark (inverted) Static Map style when the app theme is dark.
 */
export function BalenaDeviceCardMap({
  device,
  children,
}: {
  device: FleetBalenaDeviceDto;
  children?: ReactNode;
}) {
  const { $t } = useIntl();
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const [imageFailed, setImageFailed] = useState(false);

  const lat = device.coords?.lat;
  const lng = device.coords?.lng;
  const mapUrl =
    lat && lng ? buildGoogleStaticMapUrl({ lat, lng, isOnline: device.isOnline, isDark }) : null;

  // Retry the image when the URL changes (e.g. light ↔ dark theme).
  useEffect(() => {
    setImageFailed(false);
  }, [mapUrl]);

  const showImage = Boolean(mapUrl) && !imageFailed;

  return (
    <AppCardMedia sx={mapThumbSx}>
      {showImage ? (
        <Box
          component="img"
          src={mapUrl!}
          alt={$t(mapAltMessage, { name: device.name })}
          onError={() => setImageFailed(true)}
          sx={{
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            display: 'block',
          }}
        />
      ) : null}
      {children}
    </AppCardMedia>
  );
}
